/**
 * The system share sheet, and the clipboard behind it.
 *
 * WHAT THIS IS. Two ways to get a link out of the app, wrapped so the caller does not
 * have to know which one it got: `shareLink` tries the platform's own share sheet and
 * falls back to the clipboard, and reports which happened. On a phone that means the
 * real share sheet — Messages, WhatsApp, Mail, AirDrop — and on a desktop browser that
 * mostly means the clipboard, because `navigator.share` is still largely mobile.
 *
 * WHY THE RESULT IS A UNION AND NOT A BOOLEAN. There are four outcomes and three of
 * them need different words on screen:
 *
 *   'shared'    the sheet was used. Nothing more to say — the OS already showed the
 *               user where it went, and a toast on top of that is noise.
 *   'copied'    no sheet, but the link is on the clipboard. Say so, because nothing
 *               visible happened.
 *   'dismissed' the sheet opened and the user backed out. NOT a failure and must not
 *               be reported as one: an error message for a deliberate cancel teaches
 *               people that the button is broken.
 *   'failed'    neither worked. Say so, and leave the link on screen to select.
 *
 * A boolean would collapse 'dismissed' into 'failed', which is the single most common
 * bug in share buttons.
 *
 * WHAT IT DELIBERATELY DOES NOT DO is share a file or an audio track. There is no
 * audio (generation is simulated) and `navigator.share` would happily accept a `files`
 * array, so this is the boundary where it would be easy to imply a track was sent
 * when only a URL was. It shares a URL, which is what exists.
 */

/** What happened when a link was shared. See the header for why each is distinct. */
export type ShareOutcome = 'shared' | 'copied' | 'dismissed' | 'failed';

/** What to share. Mirrors the subset of ShareData this app has anything to put in. */
export interface ShareLinkRequest {
  /** The URL. The only field that must be present — it is the thing being shared. */
  url: string;
  /** Short heading. Some targets show it, many ignore it; both are fine. */
  title?: string;
  /** One line of context. Same caveat. */
  text?: string;
}

/**
 * True when the browser has a share sheet to offer.
 *
 * A function rather than a constant because it is read at click time: a module-level
 * boolean would be computed at import, which is before the tests can install or remove
 * a stub, and in a real browser is a needless assumption that capabilities never change.
 *
 * `navigator.canShare` is consulted when it exists, because Safari and Chrome both
 * expose `share` while refusing particular payloads, and a URL-only payload that
 * `canShare` rejects would otherwise throw at the point of no return.
 */
export function canSystemShare(data?: ShareLinkRequest): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return false;
  if (data && typeof navigator.canShare === 'function') {
    try {
      return navigator.canShare({ url: data.url, title: data.title, text: data.text });
    } catch {
      return false;
    }
  }
  return true;
}

/** True when there is a clipboard to write to. */
export function canCopy(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.clipboard?.writeText === 'function';
}

/** Writes text to the clipboard, reporting whether it landed. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (!canCopy()) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // A denied permission, an insecure origin, or a browser old enough not to have
    // the API. The caller shows the link for manual copying either way.
    return false;
  }
}

/**
 * Shares a link through the system sheet, falling back to the clipboard.
 *
 * The order matters: the sheet is tried first because it is what the user asked for
 * when they pressed Share, and a copy is the consolation prize. A cancel does NOT fall
 * through to a copy — the user closed the sheet, and quietly putting the link on their
 * clipboard instead is doing something they did not ask for with a thing they may have
 * deliberately decided not to send.
 */
export async function shareLink(data: ShareLinkRequest): Promise<ShareOutcome> {
  if (canSystemShare(data)) {
    try {
      await navigator.share({ url: data.url, title: data.title, text: data.text });
      return 'shared';
    } catch (error) {
      /*
        AbortError is the cancel. It is specified, it is what every browser throws when
        the sheet is dismissed, and it is indistinguishable from a real failure unless
        it is checked for by name — which is why so many share buttons show an error
        after a perfectly ordinary "not now".

        NotAllowedError is the other expected one: the call has to happen inside a user
        gesture, so a share fired from a timer or an await that lost the gesture is
        rejected. That IS a failure, but a recoverable one, so it falls through to the
        clipboard below rather than being reported.
      */
      if (error instanceof Error && error.name === 'AbortError') return 'dismissed';
    }
  }

  return (await copyText(data.url)) ? 'copied' : 'failed';
}
